/**
 * Overflow Variant Picker
 *
 * Handles products with more than 3 option axes. Option3 ("Configuration") is
 * hidden from the native picker. Two modes:
 *
 * 1. METAFIELD MODE — Extra variant metafield values are rendered as dropdowns.
 *    Matching: Option1 + Option2 + all metafield selections.
 *
 * 2. CONFIG FALLBACK MODE — No metafields exist, but Option1+Option2 can't
 *    uniquely identify a variant. Configuration (Option3) is shown as a
 *    visible dropdown. Matching: Option1 + Option2 + Option3 (config) value.
 */
class OverflowVariantPicker extends HTMLElement {
  /** @type {Array<{id:number, available:boolean, option1:string, option2:string, option3:string, price:number, compare_at_price:number|null, sku:string, metafields:Record<string,string|null>}>} */
  variants = [];

  /** @type {HTMLSelectElement[]} metafield mode selects */
  metafieldSelects = [];

  /** @type {HTMLSelectElement|null} config fallback select */
  configSelect = null;

  /** @type {'metafield'|'config'} */
  mode = 'metafield';

  connectedCallback() {
    const dataScript = this.querySelector('script[data-overflow-variants]');
    if (dataScript) {
      try {
        this.variants = JSON.parse(dataScript.textContent);
      } catch (e) {
        console.error('[overflow-picker] Failed to parse variant data:', e);
        return;
      }
    }

    // Determine mode
    this.metafieldSelects = /** @type {HTMLSelectElement[]} */ (
      Array.from(this.querySelectorAll('.overflow-metafield-select'))
    );
    this.configSelect = /** @type {HTMLSelectElement|null} */ (
      this.querySelector('.overflow-config-select')
    );

    if (this.metafieldSelects.length > 0) {
      this.mode = 'metafield';
    } else if (this.configSelect) {
      this.mode = 'config';
    } else {
      // No dropdowns at all — shouldn't happen, but bail
      return;
    }

    // Bind change listeners
    const onDropdownChange = (/** @type {HTMLSelectElement} */ select) => {
      // Clear error on this field immediately
      const wrapper = select.closest('.overflow-metafield-option, .overflow-config-option');
      if (wrapper) wrapper.removeAttribute('data-error');
      // Highlight any remaining empty fields
      this.onSelectionChange();
    };

    if (this.mode === 'metafield') {
      this.metafieldSelects.forEach((select) => {
        select.addEventListener('change', () => onDropdownChange(select));
      });
    } else if (this.configSelect) {
      this.configSelect.addEventListener('change', () => onDropdownChange(/** @type {HTMLSelectElement} */ (this.configSelect)));
    }

    // Listen for native variant updates (Option1/Option2 changes)
    const section = this.closest('.shopify-section');
    if (section) {
      section.addEventListener('variant:update', () => {
        // Run after the product form has processed the event
        requestAnimationFrame(() => {
          this.updateDropdownAvailability();
          this.onSelectionChange();
        });
      });

      section.addEventListener('variant:selected', () => {
        // Immediately disable ATC when a native option is changing
        this.disableAddToCart();
      });
    }

    // Initial state — disable ATC and highlight missing selections
    setTimeout(() => {
      this.onSelectionChange();
      this.highlightMissingSelections();
    }, 50);

    if (this.#debugEnabled()) this.#logInventoryShape();
  }

  /**
   * One-time dump on init: variants table + per-key metafield coverage.
   * Reveals at a glance whether some variants are missing the metafields the
   * dropdowns depend on — the most common cause of "no variant matches".
   */
  #logInventoryShape() {
    const keys = this.metafieldSelects.map((s) => s.dataset.metafieldKey);
    const total = this.variants.length;

    console.group(`[ovp] inventory shape — ${total} variants, ${keys.length} overflow keys: ${keys.join(', ')}`);

    const rows = this.variants.map((v) => {
      const row = { id: v.id, available: v.available, sku: v.sku, voltage: v.option1, width: v.option2 };
      for (const k of keys) row[k] = v.metafields[k] ?? '∅';
      return row;
    });
    console.table(rows);

    const coverage = keys.map((k) => {
      const populated = this.variants.filter((v) => v.metafields[k] != null && v.metafields[k] !== '').length;
      const distinct = new Set(this.variants.map((v) => v.metafields[k]).filter((x) => x != null && x !== '')).size;
      return { key: k, populated: `${populated}/${total}`, distinct_values: distinct };
    });
    console.log('per-key coverage (variants with that metafield set):');
    console.table(coverage);

    const fullyPopulated = this.variants.filter((v) => keys.every((k) => v.metafields[k] != null && v.metafields[k] !== '')).length;
    console.log(`%cvariants with ALL ${keys.length} overflow metafields populated: ${fullyPopulated}/${total}`,
      fullyPopulated === total ? 'color:green' : 'color:orange;font-weight:bold');
    if (fullyPopulated < total) {
      console.warn('[ovp] some variants are missing metafields and can never match a fully-filled selection — fix metafield data in Shopify admin');
    }
    console.groupEnd();
  }

  // ---------------------------------------------------------------------------
  // Read current selections
  // ---------------------------------------------------------------------------

  /** @returns {string|null} */
  getCurrentOption1() {
    const picker = this.closest('.shopify-section')?.querySelector('variant-picker');
    if (!picker) return null;
    const checked = picker.querySelector('fieldset[data-fieldset-index="0"] input:checked');
    if (checked) return /** @type {HTMLInputElement} */ (checked).value;
    const select = picker.querySelector('.variant-option__select');
    if (select) return /** @type {HTMLSelectElement} */ (select).value;
    return null;
  }

  /** @returns {string|null} */
  getCurrentOption2() {
    const picker = this.closest('.shopify-section')?.querySelector('variant-picker');
    if (!picker) return null;
    const checked = picker.querySelector('fieldset[data-fieldset-index="1"] input:checked');
    if (checked) return /** @type {HTMLInputElement} */ (checked).value;
    const selects = picker.querySelectorAll('.variant-option__select');
    if (selects.length > 1) return /** @type {HTMLSelectElement} */ (selects[1]).value;
    return null;
  }

  /** @returns {Record<string,string>} key → selected value (empty string if unselected) */
  getSelectedMetafields() {
    const selections = {};
    this.metafieldSelects.forEach((select) => {
      selections[select.dataset.metafieldKey] = select.value;
    });
    return selections;
  }

  /** @returns {boolean} true if all overflow dropdowns are filled */
  allSelectionsFilled() {
    if (this.mode === 'config') {
      return this.configSelect !== null && this.configSelect.value !== '';
    }
    return this.metafieldSelects.every((s) => s.value !== '');
  }

  // ---------------------------------------------------------------------------
  // Variant matching
  // ---------------------------------------------------------------------------

  /**
   * Find the single variant matching all current selections.
   * Returns null if no unique match.
   */
  findMatchingVariant() {
    const option1 = this.getCurrentOption1();
    const option2 = this.getCurrentOption2();
    const debug = this.#debugEnabled();

    if (debug) {
      console.group('[ovp] findMatchingVariant');
      console.log('option1 (Voltage):', option1);
      console.log('option2 (Width):', option2);
    }

    if (!option1) {
      if (debug) {
        console.warn('[ovp] no match — option1 (Voltage) not picked');
        console.groupEnd();
      }
      return null;
    }

    if (this.mode === 'config') {
      const configValue = this.configSelect?.value;
      if (!configValue) {
        if (debug) {
          console.warn('[ovp] no match — Configuration not picked (config mode)');
          console.groupEnd();
        }
        return null;
      }

      const result = this.variants.find((v) => {
        if (v.option1 !== option1) return false;
        if (option2 !== null && v.option2 !== option2) return false;
        if (v.option3 !== configValue) return false;
        return true;
      }) || null;

      if (debug) {
        console.log('matched (config mode):', result);
        console.groupEnd();
      }
      return result;
    }

    // Metafield mode
    const metafields = this.getSelectedMetafields();
    if (debug) console.log('overflow picks:', metafields);

    /** @type {{id:number, reason:string}[]} */
    const rejections = [];
    let matched = null;

    for (const v of this.variants) {
      if (v.option1 !== option1) {
        rejections.push({ id: v.id, reason: `option1 mismatch: variant=${v.option1} picked=${option1}` });
        continue;
      }
      if (option2 !== null && v.option2 !== option2) {
        rejections.push({ id: v.id, reason: `option2 mismatch: variant=${v.option2} picked=${option2}` });
        continue;
      }
      let mfFail = null;
      for (const [key, value] of Object.entries(metafields)) {
        if (!value) { mfFail = `${key}: nothing picked yet`; break; }
        if (v.metafields[key] == null) { mfFail = `${key}: variant has no metafield (null)`; break; }
        if (String(v.metafields[key]) !== value) {
          mfFail = `${key}: variant=${JSON.stringify(v.metafields[key])} picked=${JSON.stringify(value)}`;
          break;
        }
      }
      if (mfFail) {
        rejections.push({ id: v.id, reason: mfFail });
        continue;
      }
      matched = v;
      break;
    }

    if (debug) {
      if (matched) {
        console.log('%cMATCHED', 'color:green;font-weight:bold', { id: matched.id, available: matched.available, sku: matched.sku });
      } else {
        console.warn('[ovp] NO MATCH — top rejection reasons:');
        // show only variants that passed option1+option2 first (most relevant)
        const onMyVoltage = rejections.filter((r) => !r.reason.startsWith('option1'));
        const sample = (onMyVoltage.length ? onMyVoltage : rejections).slice(0, 10);
        console.table(sample);
        if (rejections.length > sample.length) {
          console.log(`...${rejections.length - sample.length} more rejections suppressed`);
        }
      }
      console.groupEnd();
    }

    return matched;
  }

  /**
   * Strict sequential gating: every overflow dropdown is locked until the
   * previous step is filled in. The first overflow dropdown waits on both
   * native options (Voltage + Width) being picked. If an upstream pick is
   * cleared, all downstream dropdowns disable and lose their value.
   */
  #applySequentialGating() {
    const option1 = this.getCurrentOption1();
    const option2 = this.getCurrentOption2();

    // Both native options must be filled before any overflow dropdown opens.
    let prevFilled = !!option1 && !!option2;

    this.metafieldSelects.forEach((select) => {
      if (!prevFilled) {
        if (select.value !== '') select.value = '';
        select.disabled = true;
      } else {
        select.disabled = false;
      }
      prevFilled = prevFilled && select.value !== '';
    });
  }

  /**
   * Diagnostic logging gate. Enable in the browser console via:
   *   window.OVP_DEBUG = true
   * or by appending ?ovp_debug=1 to the URL.
   * @returns {boolean}
   */
  #debugEnabled() {
    if (typeof window === 'undefined') return false;
    if (window.OVP_DEBUG === true) return true;
    try {
      return new URL(window.location.href).searchParams.get('ovp_debug') === '1';
    } catch {
      return false;
    }
  }

  // ---------------------------------------------------------------------------
  // Cascading dropdown availability
  // ---------------------------------------------------------------------------

  /**
   * Update dropdown options based on current Option1/Option2 selections.
   * Disable unreachable values and reset if the current selection is invalid.
   */
  updateDropdownAvailability() {
    const option1 = this.getCurrentOption1();
    const option2 = this.getCurrentOption2();

    if (this.mode === 'config' && this.configSelect) {
      // Filter config values by Option1 + Option2
      const reachable = new Set();
      this.variants.forEach((v) => {
        if (option1 && v.option1 !== option1) return;
        if (option2 && v.option2 !== option2) return;
        if (v.option3) reachable.add(v.option3);
      });

      const currentValue = this.configSelect.value;
      Array.from(this.configSelect.options).forEach((opt) => {
        if (opt.value === '') return;
        opt.disabled = !reachable.has(opt.value);
      });
      if (currentValue && !reachable.has(currentValue)) {
        this.configSelect.value = '';
      }
      return;
    }

    // Metafield mode — cascade across ALL current picks (Option1, Option2,
    // and every other already-filled metafield dropdown). When the product
    // matrix is sparse (each combo maps to a specific value in every
    // dimension), this is the only way to keep users from picking a combo
    // that no variant satisfies. If a previously-picked value becomes
    // unreachable as a result, it gets cleared below so the user can pick
    // something that's actually achievable.
    const otherPicks = {};
    this.metafieldSelects.forEach((s) => {
      if (s.value) otherPicks[s.dataset.metafieldKey] = s.value;
    });
    const debug = this.#debugEnabled();

    this.metafieldSelects.forEach((select) => {
      const key = select.dataset.metafieldKey;
      const currentValue = select.value;

      const reachable = new Set();

      this.variants.forEach((v) => {
        if (option1 && v.option1 !== option1) return;
        if (option2 && v.option2 !== option2) return;

        // Require every OTHER picked metafield to match (skip the key we're
        // computing reachability for — otherwise it'd only match itself).
        for (const [k, val] of Object.entries(otherPicks)) {
          if (k === key) continue;
          if (v.metafields[k] == null) return;
          if (String(v.metafields[k]) !== val) return;
        }

        if (v.metafields[key] != null && v.metafields[key] !== '') {
          reachable.add(String(v.metafields[key]));
        }
      });

      Array.from(select.options).forEach((opt) => {
        if (opt.value === '') return; // placeholder
        opt.disabled = !reachable.has(opt.value);
      });

      if (currentValue && !reachable.has(currentValue)) {
        if (debug) console.log(`[ovp] reset ${key} — previous pick "${currentValue}" no longer reachable`);
        select.value = '';
      }
    });
  }

  // ---------------------------------------------------------------------------
  // State update — called on every selection change
  // ---------------------------------------------------------------------------

  onSelectionChange() {
    this.updateDropdownAvailability();
    this.#applySequentialGating();

    const prompt = this.querySelector('.overflow-selection-prompt');
    const debug = this.#debugEnabled();

    if (!this.allSelectionsFilled()) {
      if (debug) {
        const missing = this.metafieldSelects.filter((s) => s.value === '').map((s) => s.dataset.metafieldKey);
        console.log('[ovp] ATC disabled — overflow dropdowns still empty:', missing);
      }
      if (prompt) prompt.removeAttribute('hidden');
      this.disableAddToCart();
      this.highlightMissingSelections();
      return;
    }

    const matched = this.findMatchingVariant();

    if (matched) {
      if (prompt) prompt.setAttribute('hidden', '');
      this.setVariantId(matched.id);
      this.updateSkuDisplay(matched.sku);
      this.updatePriceDisplay(matched);

      if (matched.available) {
        if (debug) console.log('%c[ovp] ATC enabled', 'color:green', { id: matched.id, sku: matched.sku });
        this.enableAddToCart();
      } else {
        if (debug) console.log('[ovp] ATC disabled — variant matched but unavailable', { id: matched.id, sku: matched.sku });
        this.disableAddToCart();
        this.setAddToCartText('Sold out');
      }
    } else {
      if (debug) console.log('[ovp] ATC disabled — no variant matches current selections (see findMatchingVariant log above)');
      if (prompt) prompt.removeAttribute('hidden');
      this.disableAddToCart();
    }
  }

  // ---------------------------------------------------------------------------
  // DOM helpers
  // ---------------------------------------------------------------------------

  /** @param {number} variantId */
  setVariantId(variantId) {
    const form = this.closest('.shopify-section')?.querySelector('product-form-component');
    const input = form?.querySelector('input[name="id"]');
    if (input) /** @type {HTMLInputElement} */ (input).value = String(variantId);

    // Update URL
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variantId));
    history.replaceState({}, '', url.toString());

    // Notify listeners (e.g. dynamic-product-description) that the resolved
    // variant changed via overflow dropdowns. The native variant:update
    // event doesn't fire here because Option1/Option2 didn't change.
    this.dispatchEvent(
      new CustomEvent('variant:matched', { bubbles: true, detail: { variantId: variantId } })
    );
  }

  /** @param {string} sku */
  updateSkuDisplay(sku) {
    const section = this.closest('.shopify-section');
    if (!section) return;
    const skuEl = section.querySelector('[data-sku]') || section.querySelector('.product-sku__value');
    if (skuEl) skuEl.textContent = sku || '';
  }

  /** @param {{id:number}} variant */
  updatePriceDisplay(variant) {
    const section = this.closest('.shopify-section');
    if (!section) return;

    // Use the server-rendered price markup for this variant (see the
    // `data-overflow-price` templates in blocks/variant-picker.liquid). This
    // keeps currency, the per-foot suffix, and compare-at/sale styling exactly
    // in parity with the `price` snippet, instead of reformatting money in JS.
    const template = /** @type {HTMLTemplateElement | null} */ (
      this.querySelector(`template[data-overflow-price="${variant.id}"]`)
    );
    const newPrice = template?.content.querySelector('[ref="priceContainer"]');
    if (!newPrice) return;

    section.querySelectorAll('product-price [ref="priceContainer"]').forEach((currentPrice) => {
      if (currentPrice.innerHTML !== newPrice.innerHTML) {
        currentPrice.innerHTML = newPrice.innerHTML;
      }
    });
  }

  disableAddToCart() {
    const btn = this.getAddToCartButton();
    if (btn) btn.disabled = true;
  }

  enableAddToCart() {
    const btn = this.getAddToCartButton();
    if (btn) btn.disabled = false;
  }

  /** @param {string} text */
  setAddToCartText(text) {
    const btn = this.getAddToCartButton();
    if (!btn) return;
    const span = btn.querySelector('.add-to-cart-text--default') || btn.querySelector('span');
    if (span) span.textContent = text;
  }

  highlightMissingSelections() {
    if (this.mode === 'metafield') {
      this.metafieldSelects.forEach((select) => {
        if (select.value === '') {
          const wrapper = select.closest('.overflow-metafield-option');
          if (wrapper) wrapper.setAttribute('data-error', '');
        }
      });
    } else if (this.configSelect && this.configSelect.value === '') {
      const wrapper = this.configSelect.closest('.overflow-config-option');
      if (wrapper) wrapper.setAttribute('data-error', '');
    }
  }

  /** @returns {HTMLButtonElement|null} */
  getAddToCartButton() {
    const section = this.closest('.shopify-section');
    if (!section) return null;
    return /** @type {HTMLButtonElement|null} */ (
      section.querySelector('add-to-cart-component [ref="addToCartButton"]') ||
        section.querySelector('button[name="add"]')
    );
  }
}

if (!customElements.get('overflow-variant-picker')) {
  customElements.define('overflow-variant-picker', OverflowVariantPicker);
}
